import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { parseAsInteger, parseAsStringLiteral, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { MessageSquareText, PauseCircle, Plus, Search } from 'lucide-react'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn, formatMoney, resultCount } from '@/lib/utils'
import {
  formatDay,
  receivableState,
  receivablesQueryOptions,
  receivablesSummaryQueryOptions,
  useReceivablesApi,
  type Receivable,
  type ReceivablesSummary,
  type ReceivableStatusFilter,
} from './api'
import { NewInvoiceDialog } from './new-invoice-dialog'
import { SearchInput } from '@/components/search-input'
import { ResultCount } from '@/components/list-filters'

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'all', label: 'All' },
] as const satisfies ReadonlyArray<{
  value: ReceivableStatusFilter
  label: string
}>

function Aging({ summary }: { summary: ReceivablesSummary }) {
  return (
    <section
      aria-label="Aging"
      className="grid grid-cols-2 overflow-hidden rounded-[10px] border bg-card sm:grid-cols-5"
    >
      {summary.aging.map((bucket) => (
        <div
          className={cn(
            'border-b px-4 py-3 sm:border-b-0 sm:border-r sm:last:border-r-0',
            bucket.key !== 'current' && Number(bucket.amount) > 0 && 'bg-red-50/60 dark:bg-red-950/20',
          )}
          key={bucket.key}
        >
          <p className="text-xs font-medium text-muted-foreground">{bucket.label}</p>
          <p className="mt-0.5 text-lg font-semibold tabular-nums">{formatMoney(bucket.amount)}</p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {bucket.count} {bucket.count === 1 ? 'invoice' : 'invoices'}
          </p>
        </div>
      ))}
    </section>
  )
}

function FollowUp({ row }: { row: Receivable }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {row.open_action_count ? (
        <StatusPill tone="danger">{row.open_action_count} to do</StatusPill>
      ) : row.unread_reply ? (
        <StatusPill tone="warning">
          <MessageSquareText aria-hidden="true" className="mr-1 size-3" />
          Customer replied
        </StatusPill>
      ) : null}
      {row.reminders_paused ? (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <PauseCircle aria-hidden="true" className="size-3.5" />
          Reminders paused
        </span>
      ) : row.next_reminder_on ? (
        <span className="text-muted-foreground">Reminder {formatDay(row.next_reminder_on)}</span>
      ) : null}
    </div>
  )
}

function CustomerCell({ row }: { row: Receivable }) {
  return (
    <div className="min-w-0">
      {row.customer_group_uuid ? (
        <Link
          className="block truncate font-medium hover:underline"
          onClick={(event) => event.stopPropagation()}
          params={{ groupId: row.customer_group_uuid }}
          to="/customers/$groupId"
        >
          {row.customer_name}
        </Link>
      ) : (
        <span className="block truncate font-medium">{row.customer_name}</span>
      )}
      <span className="block truncate text-xs text-muted-foreground">{row.recipient_email}</span>
    </div>
  )
}

function QuoteLink({ row }: { row: Receivable }) {
  if (!row.quote_id) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <Link
      className="font-mono text-xs hover:underline"
      onClick={(event) => event.stopPropagation()}
      params={{ quoteId: row.quote_id, stage: 'invoicing' }}
      to="/quotes/$quoteId/workflow/$stage"
    >
      {row.quote_number}
    </Link>
  )
}

const RECEIVABLE_STATUSES = [
  'open',
  'overdue',
  'paid',
  'void',
  'all',
] as const satisfies readonly ReceivableStatusFilter[]

/** AR Invoices: every customer invoice, its balance, aging and follow-up (USA-1094, USA-378). */
export function ReceivablesPage() {
  const api = useReceivablesApi()
  const navigate = useNavigate()
  const [status, setStatus] = useQueryState(
    'status',
    parseAsStringLiteral(RECEIVABLE_STATUSES).withDefault('open'),
  )
  const [search, setSearch] = useUrlSearchText()
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const summary = useQuery(receivablesSummaryQueryOptions(api))
  const list = useQuery(receivablesQueryOptions(api, status, search.trim(), page))
  const rows = list.data?.items ?? []
  const open = (row: Receivable) =>
    void navigate({
      to: '/receivables/$invoiceId',
      params: { invoiceId: row.invoice_id },
    })

  return (
    <div className="mx-auto max-w-7xl space-y-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">AR Invoices</h1>
        {summary.data ? (
          <p className="mt-1 text-sm text-muted-foreground tabular-nums">
            {formatMoney(summary.data.open_amount)} open
            {Number(summary.data.overdue_amount) > 0 ? (
              <span className="text-red-700 dark:text-red-300">
                {' '}
                · {formatMoney(summary.data.overdue_amount)} overdue
              </span>
            ) : null}
            {' · '}
            {formatMoney(summary.data.collected_30_days)} collected in the last 30 days
          </p>
        ) : null}
      </div>
        <NewInvoiceDialog
          openAfterCreate
          source={{}}
          trigger={
            <Button>
              <Plus aria-hidden="true" />
              New invoice
            </Button>
          }
        />
      </div>

      {summary.data ? <Aging summary={summary.data} /> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <ReviewChoiceGroup
          label="Show"
          onChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
          options={STATUS_OPTIONS}
          value={status}
        />
        <div className="flex items-center gap-3">
          <label className="relative min-w-0 flex-1 sm:w-72 sm:flex-none">
            <span className="sr-only">Search invoices</span>
            <Search
              aria-hidden="true"
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <SearchInput
              className="pl-9"
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              placeholder="Customer, quote or invoice"
              value={search}
            />
          </label>
          <ResultCount className="sm:order-first">{resultCount(list.data?.total, 'invoice')}</ResultCount>
        </div>
      </div>

      <div className="hidden lg:block">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[170px]">Invoice</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="w-[110px]">Quote</TableHead>
              <TableHead className="w-[120px]">Due</TableHead>
              <TableHead className="w-[150px]">Status</TableHead>
              <TableHead className="w-[150px] text-right">Balance</TableHead>
              <TableHead className="w-[230px]">Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const state = receivableState(row)
              return (
                <TableRow className="cursor-pointer" key={row.invoice_id} onClick={() => open(row)}>
                  <TableCell>
                    <Link
                      className="font-mono text-xs font-medium hover:underline"
                      params={{ invoiceId: row.invoice_id }}
                      to="/receivables/$invoiceId"
                    >
                      {row.invoice_number}
                    </Link>
                    <span className="block text-xs text-muted-foreground">{row.invoice_label}</span>
                  </TableCell>
                  <TableCell>
                    <CustomerCell row={row} />
                  </TableCell>
                  <TableCell>
                    <QuoteLink row={row} />
                  </TableCell>
                  <TableCell className="tabular-nums">{formatDay(row.due_at)}</TableCell>
                  <TableCell>
                    <StatusPill tone={state.tone}>{state.label}</StatusPill>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="block font-semibold">{formatMoney(row.amount_remaining)}</span>
                    <span className="block text-xs text-muted-foreground">of {formatMoney(row.amount_due)}</span>
                  </TableCell>
                  <TableCell>
                    <FollowUp row={row} />
                  </TableCell>
                </TableRow>
              )
            })}
            {!rows.length && !list.isLoading ? (
              <TableRow>
                <TableCell className="py-10 text-center text-muted-foreground" colSpan={7}>
                  No invoices
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <ul className="grid gap-3 lg:hidden">
        {rows.map((row) => {
          const state = receivableState(row)
          return (
            <li key={row.invoice_id}>
              <Link
                className="block rounded-[10px] border bg-card p-4 hover:bg-muted/30"
                params={{ invoiceId: row.invoice_id }}
                to="/receivables/$invoiceId"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{row.customer_name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {[row.invoice_number, row.quote_number].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="shrink-0 text-right tabular-nums">
                    <p className="font-semibold">{formatMoney(row.amount_remaining)}</p>
                    <p className="text-xs text-muted-foreground">Due {formatDay(row.due_at)}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusPill tone={state.tone}>{state.label}</StatusPill>
                  <FollowUp row={row} />
                </div>
              </Link>
            </li>
          )
        })}
        {!rows.length && !list.isLoading ? (
          <li className="rounded-[10px] border bg-card p-6 text-center text-sm text-muted-foreground">No invoices</li>
        ) : null}
      </ul>

      {(list.data?.pages ?? 1) > 1 ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {list.data?.page ?? 1} of {list.data?.pages ?? 1}
          </p>
          <div className="flex gap-2">
            <Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} size="sm" variant="outline">
              Previous
            </Button>
            <Button
              disabled={page >= (list.data?.pages ?? 1)}
              onClick={() => setPage((value) => value + 1)}
              size="sm"
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
